import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { noticesApi, type AppNotice } from '@/api/client';
import { useAuth } from '@/contexts/AuthContext';
import { inSpeedRun, madeDuringRun } from '@/lib/speedRun';
import { NOTIFICATION_POLL_MS } from '@/lib/notifications.core';

/****************
 * THE BELL'S NOTICES (11-03): "Your Backtest Results are Ready!" beside the
 * copied orders. Polled with the orders. During a speed run only what the run
 * made shows (the newcomer lens, as every other list). Seen = the newest id
 * the person had when they last opened the bell, kept in this browser.
 ****************/
const SEEN_KEY = 'copyfi.notices.seen';
const listeners = new Set<() => void>();
const readSeen = () => { try { return Number(localStorage.getItem(SEEN_KEY) || 0); } catch { return 0; } };

export function useNotices() {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ['notices'],
    queryFn: () => noticesApi.list(),
    enabled: Boolean(user),
    staleTime: NOTIFICATION_POLL_MS,
    refetchInterval: NOTIFICATION_POLL_MS,
    placeholderData: (previous) => previous,
  });
  const notices: AppNotice[] = useMemo(
    () => (q.data?.notices ?? []).filter((n) => !inSpeedRun() || madeDuringRun(n.created_at)),
    [q.data],
  );
  const seen = useSyncExternalStore((cb) => { listeners.add(cb); return () => listeners.delete(cb); }, readSeen, () => 0);
  const unread = notices.filter((n) => n.id > seen).length;
  const markSeen = useCallback(() => {
    const top = notices.reduce((m, n) => Math.max(m, n.id), 0);
    if (top <= readSeen()) return;
    try { localStorage.setItem(SEEN_KEY, String(top)); } catch { /* private mode */ }
    listeners.forEach((l) => l());
  }, [notices]);
  return { notices, unread, seen, markSeen };
}
